'use client';
import {FC} from 'react';
import { Quote } from '../data/quotesInterface';
import { Card, CardContent } from '@/components/ui/card';
import ShareButtons from './ShareButtons';
import { Button } from '@/components/ui/button';
import { RefreshCw, Info, BookOpen, Camera } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import QuoteImage from './QuoteImage';
import Link from 'next/link';

interface QuoteCardProps {
  quote: Quote;
  image: string;
  onRefresh: () => Promise<void>;
}

const QuoteCard: FC<QuoteCardProps> = ({ quote, image, onRefresh }) => {
  return (
    <div className="flex flex-col md:flex-row gap-8 transition-all z-40">
      {/* Image Card with Attribution */}
      <div className="w-full md:w-1/2">
        <Card className="overflow-hidden shadow-xl rounded-2xl border-0 transform hover:scale-[1.01] transition-all duration-300">
          <QuoteImage key={quote.image} image={quote.image} imageHeight={quote.imageHeight} imageWidth={quote.imageWidth}></QuoteImage>
        </Card>

        {/* Photo Attribution */}
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
          <a
            href={quote.photoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary font-medium flex items-center gap-1"
          >
          <Camera size={20} className="flex-shrink-0" /> <span className='hover:underline'>View on Unsplash by {quote.photographer}</span>
          </a>

        </p>
      </div>

      {/* Quote Card */}
      <Card className="w-full md:w-1/2 shadow-xl rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border-0">
        <CardContent className="p-10 flex flex-col justify-between min-h-[420px]">
          <div className="text-center mb-8">
            <p className="text-2xl md:text-3xl lg:text-4xl quote-text mb-6 leading-relaxed">
              "{quote.content}"
            </p>
            <p className="text-lg md:text-xl quote-author text-gray-600 dark:text-gray-400">
              — {quote.author}
            </p>

            {/* Display Quote Categories */}
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {quote.tags.map((category) => (
                <Badge
                  key={category}
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-200"
                >
                  {category}
                </Badge>
              ))}
            </div>
          </div>

          <div className="mb-6 flex flex-wrap justify-center gap-2">
            <a
              href={`https://google.com/search?q=${encodeURIComponent(quote.content)} ${encodeURIComponent(quote.author)}`}
              target="_blank"
              rel='nofollow'
              className="inline-flex items-center border border-blue-200 hover:bg-blue-50 text-blue-700 rounded-md px-6 py-2 text-sm transition-colors"
            >
              <Info size={16} className="mr-2" />
              Learn More
            </a>
            <Link
              href={`/search/?cat=${quote.tags.join(',')}`}
              rel='nofollow'
              className="inline-flex items-center rounded-full text-purple-700 group border-purple-200 hover:bg-purple-50 px-6 py-2 dark:hover:bg-purple-900/20 border text-sm transition-colors"
            >
              <BookOpen size={18} className="mr-2" />
              Related Quotes
            </Link>
          </div>

          <div className="mt-auto">
            <ShareButtons quote={quote.content} author={quote.author} quoteId={quote._id} />

            {onRefresh && <div className="mt-8 text-center">
              <Button
                onClick={async () => await onRefresh()}
                variant="outline"
                className="rounded-full group border-green-200 hover:bg-green-50 px-6 py-2 dark:hover:bg-green-900/20"
              >
                <RefreshCw size={18} className="mr-2 group-hover:rotate-180 transition-transform duration-700" />
                New Random Quote
              </Button>
            </div>
            }


          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuoteCard;
